import { useState } from 'react'
import { AuthProvider, useAuth } from '@/features/auth/AuthProvider'
import { AccountMenu } from '@/features/auth/AccountMenu'
import { MobileAccountSheet } from '@/features/auth/MobileAccountSheet'
import { SignInScreen } from '@/features/auth/SignInScreen'
import { LeadsWorkspace } from '@/features/leads/LeadsWorkspace'
import { ProUsageDashboard } from '@/features/dashboards/ProUsageDashboard'
import { AppAnalytics } from '@/features/dashboards/AppAnalytics'
import { AdminView } from '@/features/settings/AdminView'
import { EventsWorkspace } from '@/features/events/EventsWorkspace'
import { AmbassadorsWorkspace } from '@/features/ambassadors/AmbassadorsWorkspace'
import { CompetitorAnalysis } from '@/features/competitors/CompetitorAnalysis'
import { PublishingWorkspace } from '@/features/publishing/PublishingWorkspace'
import { PartnersWorkspace } from '@/features/partners/PartnersWorkspace'
import { TeamScreen } from '@/features/settings/TeamScreen'
import { OwnershipWorkspace } from '@/features/ownership/OwnershipWorkspace'
import { ChannelStats } from '@/features/publishing/ChannelStats'
import { ProductConnectBanner } from '@/features/auth/ProductConnectBanner'
import { NewsletterWorkspace } from '@/features/newsletter/NewsletterWorkspace'
import { ProductSwitch, type Product } from '@/features/newsletter/ProductSwitch'
import { Automation } from '@/features/newsletter/Automation'
import { DesktopOnly } from '@/shared/ui/DesktopOnly'
import { BottomSheet, SheetItem, SectionSwitch } from '@/shared/ui/BottomSheet'
import { MobileTabBar } from '@/shared/nav/MobileTabBar'
import { useIsMobile } from '@/shared/nav/useIsMobile'
import {
  DEFAULT_SCREEN,
  DESTINATIONS,
  destinationOf,
  firstScreenOf,
  tabsOf,
  type Destination,
  type Screen,
} from '@/shared/nav/navigation'
import { productFromUrl, useUrlScreen } from '@/shared/nav/useUrlScreen'
import { OutsideApp } from '@/features/outside/OutsideApp'
import './App.css'

/**
 * Screens the design refuses to render below 760px.
 *
 * See `DesktopOnly` for the reasoning. Kept here as data rather than as
 * conditions scattered through the render, so that adding a screen means
 * deciding whether it belongs in this list rather than forgetting to.
 */
const DESKTOP_ONLY: Partial<Record<Screen, string>> = {
  'marketing.newsletter': 'Newsletter',
  'marketing.automation': 'Email automation',
  admin: 'Admin',
}

function AuthedApp() {
  const { status, isAdmin, isStaff, user } = useAuth()
  const [screen, goToScreen] = useUrlScreen()
  // Automation still has one, because a flow belongs to one product. The
  // newsletter's went away on 17 Sep 2026 when a send gained the ability to name
  // audiences in both — see `NewsletterWorkspace`. A bookmark of the old
  // /pro/newsletter still seeds skida.pro here for the automation screen.
  const [autoProduct, setAutoProduct] = useState<Product>(() => productFromUrl() ?? 'app')
  const isMobile = useIsMobile()
  // Admin has no nav pill of its own, so it keeps the destination you came from
  // lit rather than clearing the header. Declared with the other hooks, above
  // the early returns — a hook after a conditional return is a hook that
  // sometimes does not run.
  const [lastDestination, setLastDestination] = useState<Destination>('pro')
  // Which sheet is open, if any. One piece of state rather than three booleans:
  // two sheets open at once is not a state the design has, and three booleans
  // is how you end up with it anyway.
  const [sheet, setSheet] = useState<'account' | 'sections' | null>(null)

  if (status === 'loading') {
    return <div className="boot">Signing you in…</div>
  }

  if (status !== 'authorized') {
    return <SignInScreen />
  }

  /**
   * Somebody who is not staff never reaches the CRM shell — 29 Aug 2026.
   *
   * The Firestore rules are what actually hold the line, and they would refuse
   * every read this shell makes. But "refused everywhere" is not a screen: an
   * ambassador signing in would get the whole navigation, five tabs of empty
   * tables and no explanation. Each outside role gets the one thing it is here
   * for instead.
   *
   * Before the tabs are computed and before any workspace mounts, so no query
   * is ever issued that the rules will turn down.
   */
  if (!isStaff) {
    return <OutsideApp />
  }

  const destination = destinationOf(screen) ?? lastDestination
  // An admin-only tab is left out of the row for everybody else — Ownership, as
  // in the design, where a member simply has no such tab. Whoever types the URL
  // still lands on the screen and is told who may read it; the data itself is
  // held by the Firestore rules, which is the only line that counts.
  const tabs = tabsOf(destination).filter((t) => !t.adminOnly || isAdmin)
  const onAdmin = screen === 'admin'
  const currentTab = tabs.find((t) => t.key === screen)
  const blocked = isMobile ? DESKTOP_ONLY[screen] : undefined

  const go = (next: Screen) => {
    const d = destinationOf(next)
    if (d) setLastDestination(d)
    setSheet(null)
    goToScreen(next)
  }

  const goToDestination = (d: Destination) => {
    go(firstScreenOf(d))
    // The design opens Marketing's section sheet the moment you tap its tab,
    // and only Marketing's. It is the destination with eight sections, so landing
    // silently on Publishing plan would hide the other seven behind a control
    // there is no reason to know about. Three sections do not need that.
    if (d === 'marketing') setSheet('sections')
  }

  return (
    <div className={`shell${isMobile ? ' shell--mobile' : ''}`}>
      {/* The header is a desktop-only object. On a phone its three jobs — brand,
          destination switching, account — are done by the tab bar, and keeping
          it would spend a fifth of a small screen saying "Skida". */}
      {!isMobile && (
        <header className="shell__header">
          {/* The logo carries the name; the design has no wordmark beside it.
              The app icon rather than the product logo — this is the internal
              tool, and the two are now different marks. */}
          <img className="shell__logo" src="/AppIconInternal-512.png" alt="Skida Internal" />

          <nav className="mainnav" aria-label="Sections">
            {DESTINATIONS.map((d) => (
              <button
                key={d.key}
                className={`mainnav__item${
                  !onAdmin && destination === d.key ? ' mainnav__item--active' : ''
                }`}
                onClick={() => go(firstScreenOf(d.key))}
              >
                {d.label}
              </button>
            ))}
          </nav>

          <div className="shell__spacer" />

          <button
            className="shell__admin"
            onClick={() => (onAdmin ? go(DEFAULT_SCREEN) : go('admin'))}
            aria-pressed={onAdmin}
          >
            {onAdmin ? '← Back' : 'Admin'}
          </button>
          <AccountMenu />
        </header>
      )}

      <ProductConnectBanner />

      <div className="shell__body">
        {onAdmin && !blocked ? (
          <AdminView onBack={() => go(DEFAULT_SCREEN)} />
        ) : (
          <div className="surface">
            {/* The sub-tab row belongs inside the content area, above the
                screen's own header — the design puts it there rather than in
                the app bar, so a destination reads as one place with several
                views rather than seven separate destinations.

                On a phone the row becomes a single control that opens a sheet:
                Marketing's six tabs at 150px each need 900px, which a 375px
                screen answers with three rows of chrome above every screen. */}
            {isMobile ? (
              <div className="surface__switch">
                <SectionSwitch
                  label={onAdmin ? 'Admin' : (currentTab?.label ?? '')}
                  onClick={() => setSheet('sections')}
                />
              </div>
            ) : (
              <div className="subnav" role="tablist" aria-label={destination}>
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={screen === t.key}
                    className={`subnav__item${screen === t.key ? ' subnav__item--active' : ''}`}
                    onClick={() => go(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            <div className="surface__body">
              {blocked ? (
                <DesktopOnly title={blocked} />
              ) : (
                <>
                  {screen === 'pro.crm' && <LeadsWorkspace onOpenAdmin={() => go('admin')} />}
                  {screen === 'pro.dashboard' && <ProUsageDashboard />}
                  {screen === 'app.dashboard' && <AppAnalytics />}
                  {screen === 'marketing.plan' && <PublishingWorkspace />}
                  {screen === 'marketing.events' && <EventsWorkspace />}
                  {screen === 'marketing.ambassadors' && <AmbassadorsWorkspace />}
                  {screen === 'marketing.partners' && <PartnersWorkspace />}
                  {screen === 'marketing.stats' && <ChannelStats />}
                  {screen === 'marketing.competitors' && <CompetitorAnalysis />}
                  {screen === 'internal.team' && <TeamScreen />}
                  {screen === 'internal.ownership' && <OwnershipWorkspace />}
                  {/* Which identity the mail is sent as, and where replies go.
                      A consumer audience must never arrive as Skida PRO. */}
                  {/*
                    One workspace with three views — 17 Sep 2026. The product
                    switch is gone: a send can name app and PRO audiences at
                    once, and the sending identity is chosen on the Send step
                    beside the people receiving it rather than driving both.

                    The suppression list moved to its own screen. It is not a
                    per-send concern — an address that bounced is held back from
                    every audience either identity uses — and it was competing
                    for room with the thing somebody came here to do.
                  */}
                  {screen === 'marketing.newsletter' && <NewsletterWorkspace />}
                  {screen === 'marketing.automation' && (
                    <>
                      <ProductSwitch value={autoProduct} onChange={setAutoProduct} />
                      <Automation product={autoProduct} />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {isMobile && (
        <>
          <MobileTabBar
            active={sheet === 'account' ? null : destination}
            accountActive={sheet === 'account'}
            accountPhoto={user?.photoUrl}
            onSelect={goToDestination}
            onAccount={() => setSheet(sheet === 'account' ? null : 'account')}
          />

          {sheet === 'account' && <MobileAccountSheet onClose={() => setSheet(null)} />}

          {sheet === 'sections' && (
            <BottomSheet
              title={DESTINATIONS.find((d) => d.key === destination)?.label ?? ''}
              onClose={() => setSheet(null)}
            >
              {tabs.map((t) => (
                <SheetItem
                  key={t.key}
                  label={t.label}
                  active={screen === t.key}
                  onClick={() => go(t.key)}
                />
              ))}
              {/* Admin has no tab of its own on desktop either — it lives in
                  the header, which is gone. Without this it is unreachable on a
                  phone, blocked screen or not, and "you cannot get there" is a
                  worse answer than "here is why it is not here". */}
              <SheetItem label="Admin" active={onAdmin} onClick={() => go('admin')} />
            </BottomSheet>
          )}
        </>
      )}
    </div>
  )
}

export function App() {
  return (
    <AuthProvider>
      <AuthedApp />
    </AuthProvider>
  )
}
